import { UnexpectedError } from '@watershed/errors/UnexpectedError';

import { ASTCall, stringifyExpr } from '../ast';
import { Context, Func, SqlError, SqlExpression } from '../types';
import { ExprType, stringifyType } from '../expr_types';
import {
  assertArgShape,
  assertKwargShape,
  typecheckExpression,
  TypecheckWarning,
} from '../typecheck';
import { evalExpression } from '../eval';
import { exprToSql } from '../sql';
import { safeMaxStage } from '../utils';
import { mergeErrors, traceHelper } from '../sql/internal';
import {
  RawSQL,
  /* eslint-disable @watershed/no-dangerous-raw-sql -- DuckDB usage only */
  dangerousRawSql,
  sql,
  sqlF,
} from '../../sqlTemplate';
import { bestCompatibleType } from '../utils/bestCompatibleType';
import { TypeConversionError, convertToType } from '../typeConversion';
import { BadInputError } from '@watershed/errors/BadInputError';

// Function for branching. Syntax:
// if(condition, true_value, false_value)
//
// Note that our implementation does not evaluate the branch that is not taken.
// For example, in `if(true, foo(), bar())`, `bar()` is never evaluated.
export const if_func: Func = {
  typecheck(
    ctx: Context,
    call: ASTCall,
    warnings?: Array<TypecheckWarning>
  ): ExprType {
    assertArgShape(call, 3, 3);
    assertKwargShape(call, [], false);

    const conditionType = typecheckExpression(ctx, call.args[0], warnings);
    if (conditionType.ty !== 'boolean') {
      warnings?.push(
        `First argument of if must be a boolean, got ${stringifyType(
          conditionType
        )} (${stringifyExpr(call.args[0])})`
      );
    }

    const argTypes = call.args.map((expr) =>
      typecheckExpression(ctx, expr, warnings)
    );
    const outType = bestCompatibleType(argTypes[1], argTypes[2]);

    try {
      convertToType(ctx, { fromExpr: call.args[1], toType: outType });
      convertToType(ctx, { fromExpr: call.args[2], toType: outType });
    } catch (error: any) {
      if (error instanceof TypeConversionError) {
        throw new BadInputError(
          `Arguments to \`if\` must be convertible to a common type: no common type is possible between argument types "${stringifyType(
            argTypes[1]
          )}" and "${stringifyType(argTypes[2])}"`
        );
      }
      throw error;
    }

    return outType;
  },

  eval(ctx: Context, call: ASTCall): unknown {
    const cond = evalExpression(ctx, call.args[0]);
    if (typeof cond !== 'boolean') {
      throw new UnexpectedError(
        `The condition of an if statement must be a boolean, got ${typeof cond}. Is the lscript program typechecked?`
      );
    }

    const outType = typecheckExpression(ctx, call);
    if (cond) {
      return convertToType(ctx, {
        fromExpr: call.args[1],
        toType: outType,
      }).eval();
    }
    return convertToType(ctx, {
      fromExpr: call.args[2],
      toType: outType,
    }).eval();
  },

  toSql(ctx: Context, call: ASTCall): SqlExpression {
    const outType = typecheckExpression(ctx, call);

    const condSql = exprToSql(ctx, call.args[0]);
    const trueSql = convertToType(ctx, {
      fromExpr: call.args[1],
      toType: outType,
    }).toSql();
    const falseSql = convertToType(ctx, {
      fromExpr: call.args[2],
      toType: outType,
    }).toSql();

    const argsSql = [condSql, trueSql, falseSql];

    function branch(cb: (sql: SqlExpression) => RawSQL): RawSQL {
      return sqlF`CASE WHEN ${dangerousRawSql(condSql.expr)} THEN ${cb(
        trueSql
      )} ELSE ${cb(falseSql)} END`;
    }

    let error: SqlError | null = null;
    if (argsSql[1].error || argsSql[2].error) {
      error = {
        exists: sql`${branch((b) =>
          dangerousRawSql(b.error?.exists ?? 'FALSE')
        )}`,
        expr: sql`${branch((b) => dangerousRawSql(b.error?.expr ?? 'NULL'))}`,
      };
    }

    return traceHelper(
      outType,
      {
        expr: sql`${branch((b) => dangerousRawSql(b.expr))}`,
        stage: safeMaxStage(argsSql),
        // Assumption: if one branch has a unitExpr, the other does too
        unitExpr: trueSql.unitExpr
          ? sql`${branch((b) => dangerousRawSql(b.unitExpr ?? 'NULL'))}`
          : null,
        error: mergeErrors([condSql.error, error]),
      },
      (sub) => branch((sql) => sub(sql.trace))
    );
  },
};
